import { Link } from "react-router-dom";
import { Reveal } from "@/lib/motion";
import { ArrowUpRight, Layers, MessageSquareText, Cpu, Sparkles } from "lucide-react";

const PILLARS = [
  {
    icon: Layers,
    label: "Business Structure",
    blurb: "Clear offers, defined services, and the rhythms that let a business actually run.",
    accent: "bg-brand-red",
    text: "text-brand-red",
  },
  {
    icon: MessageSquareText,
    label: "Marketing Communications",
    blurb: "Consistent messaging across the touchpoints that earn trust before, during and after the sale.",
    accent: "bg-brand-brass",
    text: "text-brand-brass",
  },
  {
    icon: Cpu,
    label: "Technology Systems",
    blurb: "Choosing the right tools and connecting them sensibly — removing the hidden double-handling.",
    accent: "bg-brand-teal",
    text: "text-brand-teal",
  },
  {
    icon: Sparkles,
    label: "Practical AI Adoption",
    blurb: "Voice capture, structured prompts and AI-supported admin — built into the workflow, not bolted on.",
    accent: "bg-ink",
    text: "text-ink",
  },
];

export default function ServicePreview() {
  // Duplicate the list for a seamless marquee loop
  const loop = [...PILLARS, ...PILLARS];

  return (
    <section
      data-testid="service-preview-section"
      className="relative py-20 md:py-28 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <Reveal className="lg:col-span-6">
            <div className="overline">What we help you reimagine</div>
            <h2 className="mt-7 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Four pillars.<br />
              <em className="font-light text-ink-soft">One connected business.</em>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8 self-end" delay={1}>
            <p className="text-base md:text-lg leading-relaxed text-ink/75 max-w-lg">
              Marketing, systems and AI aren't separate projects — they're the
              same conversation about how your business is structured, how it
              communicates, and how the work actually gets done.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Horizontal marquee of pillar cards */}
      <div
        className="relative no-scrollbar overflow-hidden"
        data-testid="pillar-scroller"
      >
        {/* Edge fade masks */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-24 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-24 bg-gradient-to-l from-white to-transparent z-10" />

        <div className="marquee-track flex gap-5 md:gap-6 w-max">
          {loop.map((p, i) => {
            const Icon = p.icon;
            return (
              <Link
                key={`${p.label}-${i}`}
                to="/services"
                data-testid={`pillar-preview-${(i % PILLARS.length) + 1}`}
                className="group relative w-[260px] md:w-[320px] shrink-0 bg-white border border-ink/15 p-6 md:p-7 transition-all hover:border-ink hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(15,17,21,0.25)]"
              >
                {/* Top accent strip */}
                <span
                  className={`absolute top-0 left-0 h-[3px] ${p.accent} w-12 transition-all duration-500 group-hover:w-full`}
                />
                <div className="flex items-center justify-between">
                  <Icon size={18} strokeWidth={1.5} className={p.text} />
                  <span className="overline text-ink-soft">
                    {String((i % PILLARS.length) + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-10 md:mt-12 font-serif-display text-xl md:text-2xl leading-tight tracking-tight">
                  {p.label}
                </h3>
                <p className="mt-3 text-[13.5px] md:text-sm text-ink/70 leading-relaxed">
                  {p.blurb}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-[12px] tracking-[0.06em] font-medium text-ink-soft group-hover:text-ink transition-colors">
                  Read more
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal delay={1}>
          <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-ink-soft text-[15px] max-w-md">
              See how each pillar shows up in real engagements.
            </p>
            <Link
              to="/services"
              data-testid="service-preview-cta"
              className="group inline-flex items-center gap-3 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3.5 text-[13px] tracking-[0.06em] font-medium transition-colors"
            >
              Explore all services
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
